import React, { useState, useEffect } from "react";
import { Input, Select, notification, Divider, Row, Col, Spin } from "antd";
import MaskedInput from "antd-mask-input";
import moment from "moment";
import locale from "antd/es/date-picker/locale/pt_BR";
import axios from "axios";
import api from "../../services/api";
import { onlyNumbers } from "../../services/onlyNumber";
import paraguaiDistrict from "../../services/allParaguayDistrict";
import VerificationPhone from "../VerificationPhone";

import {
  atuationArea,
  genderOptions,
  maritalStatus,
  scholarityOptions,
} from "../../models/franchiseeInfos";

import {
  Container,
  StyledModal,
  ButtonCancel,
  ButtonSave,
  BtnsContainer,
  DatePicker,
  Form,
  FormItem,
  LoadingContainer,
  ProfileImageContainer,
  ProfileImage,
  ProfileImageUploadOverlay,
  Alert,
} from "./styles";
import Upload from "../Upload";
import AvatarUser from "../../assets/image/avatar_user.png";

interface IUserProfileModalProps {
  visible: boolean;
  onClose: () => void;
  userData: any;
  onSaved?: () => void;
}

const UserProfileModal: React.FC<IUserProfileModalProps> = ({
  visible,
  onClose,
  userData,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectCountryInput, setSelecteCountryInput] = useState("BR");
  const [selectedScholarity, setSelectedScholarity] = useState<
    number | undefined
  >();
  const [isAnotherArea, setIsAnotherArea] = useState<string | undefined>();
  const [userProfileData, setUserProfileData] = useState<any>(null);
  const [cepFilled, setCepFilled] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [loadingCep, setLoadingCep] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [pendingUserData, setPendingUserData] = useState<any>(null);

  useEffect(() => {
    if (visible && userData?.id) {
      fetchUserData(userData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, userData]);

  const handleClose = () => {
    setLoading(false);
    setSelecteCountryInput("BR");
    setIsAnotherArea(undefined);
    setUserProfileData(null);
    setCepFilled(false);
    setImage(null);
    setCurrentStep(0);
    setPendingUserData(null);
    form.resetFields();
    onClose();
  };

  const fetchUserData = async (userId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/user/${userId}`);
      setUserProfileData(response.data.content);
      setupForm(response.data.content);
    } catch (error) {
      console.error("Erro ao buscar dados do usuário:", error);
      notification.error({
        message: "Erro ao buscar dados",
        description: "Não foi possível carregar seus dados de perfil.",
      });
      setupForm(userData);
    } finally {
      setLoading(false);
    }
  };

  const setupForm = (data: any) => {
    if (!data) return;

    setIsAnotherArea(data?.atuation);
    setSelecteCountryInput(data?.cpf ? "BR" : "PY");
    setSelectedScholarity(data?.scholarity);

    if (data?.cep && data.cep.length > 0) {
      setCepFilled(true);
    }

    form.setFieldsValue({
      ...data,
      country_code: data?.country_code || (data?.cpf ? "+55" : "+595"),
      birthday: data?.birthday ? moment(data?.birthday) : null,
    });
  };

  const handleChangeCep = async (cep) => {
    const _cep = onlyNumbers(cep.target.value)?.toString();

    if (!_cep || _cep.length === 0) {
      setCepFilled(false);
      return;
    }

    setCepFilled(true);

    if (_cep?.length === 8) {
      try {
        setLoadingCep(true);
        const { data } = await axios({
          url: `https://viacep.com.br/ws/${_cep}/json/`,
          method: "GET",
        });

        if (!data.erro) {
          form.setFieldsValue({
            state: data?.uf || "",
            city: data?.localidade || "",
            district: data?.bairro || "",
            address: data?.logradouro || "",
            complement: data?.complemento !== "" ? data?.complemento : "",
          });
        }
      } catch (error) {
        notification.error({
          message: "Erro ao buscar CEP",
          description: "Não foi possível encontrar os dados do CEP informado.",
        });
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const isPhoneChanged = (newPhone: string) => {
    if (!userProfileData?.phone) return true;
    const formattedNewPhone = onlyNumbers(newPhone)?.toString();
    const formattedOldPhone = userProfileData.phone.toString();
    return formattedNewPhone !== formattedOldPhone;
  };

  const completeUpdate = async () => {
    try {
      setLoading(true);

      if (image && pendingUserData) {
        if (userProfileData?.image_key) {
          await api.delete(`/s3-upload/${userProfileData.image_key}`);
        }

        const imageToUpload = new FormData();
        imageToUpload.append("file", image as File);

        const {
          data: { location, key },
        } = await api.post("/s3-upload/upload", imageToUpload);

        pendingUserData.image = location;
        pendingUserData.image_key = key;
      }

      pendingUserData.pin_phone = null;

      await api.put(`/user/${userData.id}`, pendingUserData);

      notification.success({
        message: "Perfil atualizado com sucesso",
        duration: 3,
      });

      if (onSaved) {
        onSaved();
      }

      handleClose();
    } catch (err: any) {
      let errorMessage = "Um erro ocorreu ao salvar seus dados";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      notification.warning({
        message: "Erro ao atualizar perfil",
        description: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(0);

    if (pendingUserData) {
      form.setFieldsValue({
        ...pendingUserData,
        phone: pendingUserData.phone,
        birthday: pendingUserData.birthday
          ? moment(pendingUserData.birthday)
          : null,
      });
    } else if (userProfileData) {
      setupForm(userProfileData);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      let payload = await form.getFieldsValue();

      if (
        payload.atuation === "Outro (especificar)" &&
        payload.other_atuation
      ) {
        payload.atuation = payload.other_atuation;
        delete payload.other_atuation;
      }

      if (payload.birthday) {
        payload.birthday = moment(payload.birthday).set({
          hour: 12,
          minute: 0,
          second: 0,
        });
      }

      const formattedPhone = onlyNumbers(payload.phone)?.toString();
      payload.phone = formattedPhone;

      if (payload.cep) {
        payload.cep = onlyNumbers(payload.cep)?.toString();
      }

      if (selectCountryInput === "BR") {
        if (payload.cpf) {
          payload.cpf = onlyNumbers(payload.cpf)?.toString();
        }
      } else {
        payload.ruc = payload.ruc?.toString();
      }

      if (
        !payload.ruc &&
        !userProfileData?.ruc &&
        isPhoneChanged(payload.phone)
      ) {
        setPendingUserData(payload);
        setCurrentStep(1);
        return;
      }

      if (image) {
        if (userProfileData?.image_key) {
          await api.delete(`/s3-upload/${userProfileData.image_key}`);
        }

        const imageToUpload = new FormData();

        imageToUpload.append("file", image as File);

        const {
          data: { location, key },
        } = await api.post("/s3-upload/upload", imageToUpload);

        payload.image = location;
        payload.image_key = key;
      }

      await api.put(`/user/${userData.id}`, payload);

      notification.success({
        message: "Perfil atualizado com sucesso",
        duration: 3,
      });

      if (onSaved) {
        onSaved();
      }

      onClose();
    } catch (err: any) {
      let errorMessage = "Um erro ocorreu ao salvar seus dados";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      notification.warning({
        message: "Erro ao atualizar perfil",
        description: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFooter = () => {
    if (currentStep === 0) {
      return (
        <BtnsContainer>
          <ButtonCancel onClick={handleClose}>Cancelar</ButtonCancel>
          <ButtonSave type="primary" loading={loading} onClick={onFinish}>
            Salvar Alterações
          </ButtonSave>
        </BtnsContainer>
      );
    } else if (currentStep === 1) {
      return null;
    }
    return null;
  };

  const renderVerificationStep = () => {
    return (
      <VerificationPhone
        userId={userData.id}
        phoneNumber={pendingUserData?.phone || ""}
        onVerified={completeUpdate}
        onCancel={handleBack}
        title="Verificação de Número de Telefone"
        showCancelConfirmation={true}
      />
    );
  };

  return (
    <StyledModal
      title="Meu Perfil"
      visible={visible}
      closable={true}
      onCancel={handleClose}
      centered
      width={800}
      footer={renderFooter()}
      destroyOnClose
    >
      <Container>
        {loading && !userProfileData ? (
          <LoadingContainer>
            <Spin tip="Carregando informações..." />
          </LoadingContainer>
        ) : currentStep === 0 ? (
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            autoComplete="off"
            preserve={false}
          >
            <Divider orientation="left">Informações Pessoais</Divider>
            <Row gutter={12}>
              <Col sm={24} xs={24}>
                <FormItem
                  label="Imagem de Perfil"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ProfileImageContainer>
                    {userProfileData?.image || image ? (
                      <ProfileImage
                        src={
                          image
                            ? URL.createObjectURL(image)
                            : userProfileData?.image
                        }
                        alt="Foto de perfil"
                      />
                    ) : (
                      <ProfileImage
                        src={AvatarUser}
                        alt="Foto de perfil padrão"
                      />
                    )}
                    <ProfileImageUploadOverlay className="profile-upload">
                      <Upload
                        file={image}
                        setFile={setImage}
                        isProfileImage={true}
                        typeFile="image/*"
                      />
                    </ProfileImageUploadOverlay>
                  </ProfileImageContainer>
                  {image && (
                    <Alert
                      message="A alteração da imagem de perfil será visível apenas após o próximo login."
                      type="info"
                      showIcon
                    />
                  )}
                </FormItem>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col sm={12} xs={24}>
                <FormItem label="Nome" name="name">
                  <Input placeholder="Nome" />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem label="Sobrenome" name="last_name">
                  <Input placeholder="Sobrenome" />
                </FormItem>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col sm={12} xs={24}>
                <FormItem label="Gênero" name="gender">
                  <Select
                    placeholder="Selecione o gênero"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {genderOptions.map((option) => (
                      <Select.Option value={option.value} key={option.value}>
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col sm={12} xs={24}>
                <FormItem label="Data de nascimento" name="birthday">
                  <DatePicker
                    placeholder="Data de nascimento"
                    name="birthday"
                    format="DD/MM/YYYY"
                    locale={locale}
                    inputReadOnly={false}
                    allowClear={true}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={6}>
              <Col sm={4} xs={8}>
                <FormItem label="Código" name="country_code">
                  <Select>
                    <Select.Option value="+55">+55</Select.Option>
                    <Select.Option value="+595">+595</Select.Option>
                  </Select>
                </FormItem>
              </Col>
              <Col sm={8} xs={16}>
                <FormItem
                  label="Telefone"
                  name="phone"
                  rules={[
                    {
                      required: true,
                      message: "Telefone é obrigatório",
                    },
                  ]}
                >
                  <MaskedInput
                    name="phone"
                    mask="(11) 11111-1111"
                    placeholder="(99) 99999-9999"
                  />
                </FormItem>
              </Col>
              <Col sm={12} xs={24}>
                <FormItem label="Grau de escolaridade" name="scholarity">
                  <Select
                    placeholder="Selecione o grau de escolaridade"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) =>
                      setSelectedScholarity(value as number | undefined)
                    }
                    dropdownRender={(menu) => menu}
                  >
                    {scholarityOptions.map((option) => (
                      <Select.Option value={option.value} key={option.value}>
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col sm={12} xs={24}>
                <FormItem label="Atuação" name="atuation">
                  <Select
                    placeholder="Selecione a área de atuação"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={(value) => setIsAnotherArea(value as string)}
                  >
                    {atuationArea.map((option) => (
                      <Select.Option
                        value={option.description}
                        key={option.value}
                      >
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>

                {isAnotherArea === "Outro (especificar)" && (
                  <FormItem
                    label="Outra área de atuação"
                    name="other_atuation"
                    rules={[
                      {
                        required: true,
                        message: "A atuação é obrigatória",
                      },
                    ]}
                  >
                    <Input
                      maxLength={50}
                      name="other_atuation"
                      placeholder="Digite a área de atuação"
                    />
                  </FormItem>
                )}
              </Col>

              <Col sm={12} xs={24}>
                <FormItem label="Estado civil" name="marital_status">
                  <Select
                    placeholder="Selecione o estado civil"
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {maritalStatus.map((option) => (
                      <Select.Option value={option.value} key={option.value}>
                        {option.description}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
            </Row>

            {selectedScholarity && selectedScholarity >= 6 ? (
              <Row gutter={12}>
                <Col sm={24} xs={24}>
                  <FormItem label="Cursos/Graduações" name="courses">
                    <Input
                      name="courses"
                      placeholder="Informe seus cursos/graduações"
                    />
                  </FormItem>
                </Col>
              </Row>
            ) : null}

            <Divider orientation="left">Localização</Divider>

            {selectCountryInput === "BR" ? (
              <>
                <Row gutter={12}>
                  <Col sm={12} xs={24}>
                    <FormItem label="CEP" name="cep">
                      <MaskedInput
                        name="cep"
                        mask="11111-111"
                        onChange={(value) => handleChangeCep(value)}
                        placeholder="00000-000"
                      />
                    </FormItem>
                  </Col>
                  <Col sm={12} xs={24}>
                    <FormItem label="Endereço" name="address">
                      <Input
                        disabled={!cepFilled}
                        name="address"
                        placeholder="Preencha com o endereço"
                        suffix={loadingCep ? <Spin size="small" /> : null}
                      />
                    </FormItem>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col sm={12} xs={24}>
                    <FormItem label="Complemento" name="complement">
                      <Input
                        disabled={!cepFilled}
                        name="complement"
                        placeholder="Complemento"
                        suffix={loadingCep ? <Spin size="small" /> : null}
                      />
                    </FormItem>
                  </Col>
                  <Col sm={12} xs={24}>
                    <FormItem label="Bairro" name="district">
                      <Input
                        disabled={!cepFilled}
                        name="district"
                        placeholder="Preencha com o bairro"
                        suffix={loadingCep ? <Spin size="small" /> : null}
                      />
                    </FormItem>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col sm={12} xs={24}>
                    <FormItem label="Estado" name="state">
                      <Input
                        disabled={!cepFilled}
                        name="state"
                        placeholder="Preencha com o Estado"
                        suffix={loadingCep ? <Spin size="small" /> : null}
                      />
                    </FormItem>
                  </Col>
                  <Col sm={12} xs={24}>
                    <FormItem label="Cidade" name="city">
                      <Input
                        disabled={!cepFilled}
                        name="city"
                        placeholder="Preencha com a cidade"
                        suffix={loadingCep ? <Spin size="small" /> : null}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </>
            ) : (
              <Row gutter={12}>
                <Col sm={12} xs={24}>
                  <FormItem
                    label="CEP"
                    name="cep"
                    rules={[
                      {
                        required: true,
                        message: "CEP é obrigatório",
                      },
                    ]}
                  >
                    <MaskedInput
                      placeholder="CEP"
                      mask="111111"
                      name="cep"
                      onChange={(value) => {
                        const _cep = onlyNumbers(
                          value.target.value
                        )?.toString();
                        setCepFilled(_cep ? _cep.length > 0 : false);
                      }}
                    />
                  </FormItem>
                </Col>

                <Col sm={12} xs={24}>
                  <FormItem
                    label="Departamento"
                    name="distrito_py"
                    rules={[
                      {
                        required: true,
                        message: "Departamento é obrigatório",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Departamento"
                      showSearch
                      disabled={!cepFilled}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {paraguaiDistrict.map((state) => (
                        <Select.Option value={state} key={state}>
                          {state}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                </Col>

                <Col sm={12} xs={24}>
                  <FormItem
                    label="Cidade"
                    name="city"
                    rules={[
                      {
                        required: true,
                        message: "Cidade é obrigatório",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      name="city"
                      disabled={!cepFilled}
                      placeholder="Cidade"
                      maxLength={70}
                    />
                  </FormItem>
                </Col>

                <Col sm={12} xs={24}>
                  <FormItem
                    label="Endereço"
                    name="address"
                    rules={[
                      {
                        required: true,
                        message: "Endereço é obrigatório",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      name="address"
                      disabled={!cepFilled}
                      placeholder="Endereço"
                      maxLength={100}
                    />
                  </FormItem>
                </Col>

                <Col sm={12} xs={24}>
                  <FormItem
                    label="Bairro"
                    name="district"
                    rules={[
                      {
                        required: true,
                        message: "Bairro é obrigatório",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      name="district"
                      disabled={!cepFilled}
                      placeholder="Bairro"
                    />
                  </FormItem>
                </Col>

                <Col sm={12} xs={24}>
                  <FormItem
                    label="Complemento"
                    name="complement"
                    rules={[
                      {
                        required: false,
                        message: "Complemento é obrigatório",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      name="complement"
                      disabled={!cepFilled}
                      placeholder="Complemento"
                      maxLength={30}
                    />
                  </FormItem>
                </Col>
              </Row>
            )}
          </Form>
        ) : (
          renderVerificationStep()
        )}
      </Container>
    </StyledModal>
  );
};

export default UserProfileModal;
